var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { dia, ui, shapes, highlighters } from '@joint/plus';
import { DEFAULT_TEXT_ATTRIBUTES } from './config';
import { RichTextToolbar } from './rich-text-toolbar';
import { TextNode, textNodes } from './shapes';
export var init = function () {
    var canvasEl = document.getElementById('canvas');
    var toolbarEl = document.getElementById('toolbar');
    var shapeNamespace = __assign(__assign({}, shapes), { TextNode: TextNode });
    var graph = new dia.Graph({}, {
        cellNamespace: shapeNamespace
    });
    var paper = new dia.Paper({
        model: graph,
        width: 1000,
        height: 600,
        gridSize: 10,
        interactive: true,
        async: true,
        background: { color: '#F3F7F6' },
        clickThreshold: 10,
        cellViewNamespace: shapeNamespace
    });
    var scroller = new ui.PaperScroller({
        paper: paper,
        autoResizePaper: true,
        cursor: 'grab',
        baseWidth: 100,
        baseHeight: 100,
        contentOptions: {
            allowNewOrigin: 'negative',
            useModelGeometry: true,
            padding: 50
        }
    });
    canvasEl.appendChild(scroller.el);
    scroller.render().center();
    var toolbar = new RichTextToolbar({
        el: toolbarEl,
        defaultAttributes: DEFAULT_TEXT_ATTRIBUTES,
        setAttributes: function (attrs) {
            var model = getSelected();
            if (!model)
                return;
            var text = model.prop(TextNode.LABEL_PATH);
            var annotations = model.prop(TextNode.ANNOTATION_PATH);
            var ed = ui.TextEditor.ed;
            // If some text is selected and the user changes an attribute via the toolbar,
            // apply this change on the selected text.
            // Otherwise, set the current annotation so that the very next insert
            // will use it for the inserted text.
            var start, end;
            if (ed) {
                var range = ed.getSelectionRange();
                start = range.start;
                end = range.end;
            }
            else {
                start = 0;
                end = text.length;
            }
            var newAnnotation;
            if (start !== end) {
                newAnnotation = {
                    start: ui.TextEditor.isLineStart(text, start) ? start - 1 : start,
                    end: end,
                    attrs: attrs
                };
            }
            else if (ui.TextEditor.isEmptyLine(text, start)) {
                newAnnotation = {
                    start: start - 1,
                    end: start,
                    attrs: attrs
                };
            }
            if (newAnnotation) {
                var newAnnotations = ui.TextEditor.normalizeAnnotations(__spreadArray(__spreadArray([], annotations, true), [newAnnotation], false));
                model.prop(TextNode.ANNOTATION_PATH, newAnnotations, { rewrite: true, async: false });
                ed === null || ed === void 0 ? void 0 : ed.setAnnotations(newAnnotations);
                ed === null || ed === void 0 ? void 0 : ed.select(start, end);
            }
            else {
                ed === null || ed === void 0 ? void 0 : ed.setCurrentAnnotation(attrs);
                // Reflect the visual change of the attrs.
                // e.g. increase the size of the caret when the font-size is changed.
                ed === null || ed === void 0 ? void 0 : ed.updateCaret();
            }
        }
    });
    toolbar.disable();
    ui.TextEditor.on('caret:change select:change', function () {
        var ed = ui.TextEditor.ed;
        // Update the widgets in the toolbar based on the text annotation under the cursor
        // or in the current selection if there is one.
        var annotations = ed.getAnnotations();
        var _a = ed.getSelectionRange(), start = _a.start, end = _a.end;
        if (start === end) {
            start = end = start - 1;
        }
        var attrs = ui.TextEditor.getCombinedAnnotationAttrsBetweenIndexes(__spreadArray([{ start: -Infinity, end: Infinity, attrs: DEFAULT_TEXT_ATTRIBUTES }], annotations, true), start, end);
        toolbar.updateInputsDebounced(attrs);
    });
    graph.addCells(textNodes, { async: false });
    // Resize the elements based on the text inside them
    TextNode.setupAutoSizeAdjustment(paper);
    // Selection
    var SELECTED = 'selected';
    function unselectAll() {
        graph.getElements().filter(function (el) { return el.get(SELECTED); }).forEach(function (el) {
            var _a;
            el.set((_a = {},
                _a[SELECTED] = false,
                _a.z = 1,
                _a));
        });
        toolbar.disable();
    }
    function selectOne(el) {
        var _a;
        unselectAll();
        el.set((_a = {},
            _a[SELECTED] = true,
            _a.z = 2,
            _a));
        toolbar.enable();
    }
    function getSelected() {
        var _a;
        return (_a = graph.getElements().find(function (el) { return el.get(SELECTED); })) !== null && _a !== void 0 ? _a : null;
    }
    function isSelected(el) {
        return Boolean(el.get(SELECTED));
    }
    graph.on('change:selected', function (el) {
        var highlighterId = 'selection-mask';
        var selected = isSelected(el);
        var elView = el.findView(paper);
        if (selected) {
            highlighters.stroke.add(elView, 'body', highlighterId, {
                layer: dia.Paper.Layers.FRONT,
                useFirstSubpath: true,
                padding: 10,
                attrs: {
                    'stroke': '#0156D2',
                    'stroke-width': 3,
                    'stroke-linejoin': 'round'
                }
            });
        }
        else {
            highlighters.stroke.remove(elView, highlighterId);
        }
    });
    // Register events
    paper.on('paper:pinch', function (evt, ox, oy, scale) {
        evt.preventDefault();
        scroller.zoom(scale - 1, { min: 0.2, max: 5, ox: ox, oy: oy });
    });
    paper.on('paper:pan', function (evt, tx, ty) {
        evt.preventDefault();
        scroller.el.scrollLeft += tx;
        scroller.el.scrollTop += ty;
    });
    paper.on('blank:pointerdown', function (evt) {
        unselectAll();
        scroller.startPanning(evt);
    });
    paper.on('scale', function () {
        var _a;
        (_a = ui.TextEditor.ed) === null || _a === void 0 ? void 0 : _a.updateCaret();
    });
    paper.on('element:pointerclick', function (elementView, evt) {
        var model = elementView.model;
        if (ui.TextEditor.ed)
            return;
        var detail = evt.detail;
        if (detail === 1) {
            if (!isSelected(model)) {
                // The first click selects the element.
                var text = model.prop(TextNode.LABEL_PATH);
                var annotations = __spreadArray([
                    { start: 0, end: text.length, attrs: DEFAULT_TEXT_ATTRIBUTES }
                ], model.prop(TextNode.ANNOTATION_PATH), true);
                var attrs = ui.TextEditor.getCombinedAnnotationAttrsBetweenIndexes(annotations, 0, text.length);
                toolbar.updateInputs(attrs);
                selectOne(model);
                return;
            }
        }
        // If the element is selected, the first click opens the text editor
        // The second click opens the text editor and selects the word under the pointer.
        var labelNode = elementView.findNode('label');
        ui.TextEditor.edit(labelNode, {
            cellView: elementView,
            textProperty: TextNode.LABEL_PATH,
            annotationsProperty: TextNode.ANNOTATION_PATH,
            onOutsidePointerdown: function (pointerEvt, editor) {
                var target = pointerEvt.target;
                // If the user clicks not on the text but on the background (the cell view)
                // we set the cursor to the closest line to the click position
                if (elementView.el.contains(target)) {
                    pointerEvt.stopPropagation();
                    editor.startSelecting();
                    var charNum = editor.getCharNumFromEvent(pointerEvt);
                    editor.select(charNum);
                    return;
                }
                // If the user clicks on the toolbar we do not want to
                // cancel the current text selection
                // i.e. preventDefault() must be called.
                if (toolbar.el.contains(target)) {
                    // There is no INPUT / TEXTAREA in our toolbar. We can safely prevent the default.
                    pointerEvt.preventDefault();
                    return;
                }
                // If the user clicks outside the text editor, we'll close the editor.
                pointerEvt.stopPropagation();
                ui.TextEditor.close();
            },
            onKeydown: function (keyboardEvt) {
                switch (keyboardEvt.code) {
                    case 'Escape': {
                        keyboardEvt.stopPropagation();
                        ui.TextEditor.close();
                        unselectAll();
                        break;
                    }
                    case 'Tab': {
                        // Disable the Tab key while the text editor is open
                        keyboardEvt.preventDefault();
                        break;
                    }
                }
            },
        });
        var ed = ui.TextEditor.ed;
        var charNumFromEvent = ed.getCharNumFromEvent(evt);
        if (detail === 1) {
            // The first click on already selected element sets the cursor under the pointer
            ed.select(charNumFromEvent);
        }
        else {
            // The second click on already selected element selects a word under the pointer
            var _a = ed.getWordBoundary(charNumFromEvent), selectionStart = _a[0], selectionEnd = _a[1];
            ed.select(selectionStart, selectionEnd);
        }
    });
};
